import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Stack, Switch } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { renderTooltipIcon } from 'components/TableauObs/common/KpiCard';
import { formatValue } from 'components/PowerBIObservability/UserActivity/helper';

export interface LicenseModelItem {
    name: string;
    value: number;
    cost?: number;
}

interface LicenseModelDistributionWidgetProps {
    data: LicenseModelItem[];
    colorMapping: { [key: string]: string };
    isLoading?: boolean;
    title?: string;
    hasTooltip?: boolean;
    tooltipText?: string;
    centerLabel?: string;
    theme?: 'light' | 'dark';
    containerProps?: { sx?: object };
    showValueAsCount?:boolean,
    valueSuffix?:string,
    hasToggle?: boolean;
    viewType?: 'cost' | 'count';
    onViewTypeChange?: (view: 'cost' | 'count') => void;
}

const LicenseModelDistributionWidget: React.FC<LicenseModelDistributionWidgetProps> = ({
    data = [],
    colorMapping = {},
    isLoading = false,
    title,
    hasTooltip = false,
    tooltipText,
    centerLabel,
    containerProps = {},
    theme = 'light',
    showValueAsCount=false,
    valueSuffix="",
    hasToggle=false,
    viewType: externalViewType,
    onViewTypeChange
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [internalViewType, setInternalViewType] = useState<'cost' | 'count'>('cost');
    
    const viewType = externalViewType ?? internalViewType;

    const handleViewTypeChange = (newView: 'cost' | 'count') => {
        setInternalViewType(newView);
        if (onViewTypeChange) {
            onViewTypeChange(newView);
        }
    };
    const [windowSize, setWindowSize] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const currentValue = (item: LicenseModelItem) => (viewType === 'cost' && hasToggle) ? (item.cost || 0) : (item.value || 0);
    const total = data.reduce((sum, item) => sum + currentValue(item), 0);

    const seriesData = data.map((item) => ({
        name: item.name,
        value: currentValue(item),
        itemStyle: {
            color: colorMapping[item.name] || '#4A90E2',
        },
    }));

    const chartOption = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            formatter: (params: any) => {
                const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0.0';
                const isCost = hasToggle && viewType === 'cost';
                const formattedValue = isCost ? `$${formatValue(params.value)}` : params.value;
                return `${params.name}: ${formattedValue} (${pct}%)`;
            },
        },
        legend: {
            show: false,
        },
        series: [
            {
                type: 'pie',
                radius: ['70%', '90%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: false,
                label: { show: false },
                labelLine: { show: false },
                emphasis: {
                    scale: true,
                    scaleSize: 6,
                    label: { show: false },
                },
                data: seriesData,
            },
        ],
        graphic: [
            {
                type: 'text',
                left: 'center',
                top: '42%',
                style: {
                    text: (hasToggle && viewType === 'cost') ? `$${formatValue(total)}` : String(total),
                    textAlign: 'center',
                    fill: '#1a1a2e',
                    fontSize: (hasToggle && viewType === 'cost') ? 22 : 28,
                    fontWeight: 'bold',
                    fontFamily: 'Montserrat, sans-serif',
                },
            },
            {
                type: 'text',
                left: 'center',
                top: '55%',
                style: {
                    text: centerLabel,
                    textAlign: 'center',
                    fill: '#8a8a9a',
                    fontSize: 13,
                    fontFamily: 'Montserrat, sans-serif',
                },
            },
        ],
    };

    const noDataOption = {
        backgroundColor: 'transparent',
        title: {
            show: true,
            text: 'No data',
            left: 'center',
            top: 'center',
            textStyle: {
                color: '#aaa',
                fontWeight: 'normal',
                fontSize: 14,
            },
        },
        series: [],
    };

    return (
        <Box
            sx={{
                minHeight: '318px',
                backgroundColor: '#ffffff',
                borderRadius: '5px',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                ...containerProps?.sx,
            }}
        >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Stack direction="row" alignItems="center" gap="16px">
                    <Typography
                        sx={{ color: '#1a1a2e', fontSize: 20, fontWeight: 500 }}
                    >
                        {title}
                    </Typography>
                    {hasTooltip && renderTooltipIcon(tooltipText || "", theme)}
                </Stack>
                {hasToggle && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: 14, fontWeight: viewType === 'cost' ? 600 : 400, color: viewType === 'cost' ? '#1a1a2e' : '#8a8a9a' }}>Cost</Typography>
                        <Switch
                            checked={viewType === 'count'}
                            onChange={(e) => handleViewTypeChange(e.target.checked ? 'count' : 'cost')}
                            color="info"
                            size="small"
                        />
                        <Typography sx={{ fontSize: 14, fontWeight: viewType === 'count' ? 600 : 400, color: viewType === 'count' ? '#1a1a2e' : '#8a8a9a' }}>Count</Typography>
                    </Stack>
                )}
            </Stack>

            {/* Chart + Legend */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '200px',
                    position: 'relative',
                }}
                ref={chartContainerRef}
                key={windowSize}
            >
                {isLoading ? (
                    <LoaderComponent />
                ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ position: 'relative', width: '55%', height: '220px' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: '-20px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '120px',
                                    height: '30px',
                                    background: 'rgba(0,0,0,0.15)',
                                    filter: 'blur(8px)',
                                    borderRadius: '50%',
                                    zIndex: 0,
                                }}
                            />
                            <ReactECharts
                                style={{ width: '100%', height: '100%', position: 'relative', zIndex: 1 }}
                                option={data.length > 0 ? chartOption : noDataOption}
                                opts={{ renderer: 'canvas' }}
                                notMerge={true}
                                lazyUpdate={true}
                            />
                        </Box>
                        {/* Custom Legend */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                ml: 1,
                                flex: 1,
                            }}
                        >
                            {data.map((item) => {
                                const val = currentValue(item);
                                const pct =
                                    total > 0
                                        ? ((val / total) * 100).toFixed(1)
                                        : '0.0';
                                 const isCost = hasToggle && viewType === 'cost';
                                 
                                 let displayValue = '';
                                 if (isCost) {
                                     displayValue = `${pct}% $${formatValue(val)}`;
                                 } else if (showValueAsCount) {
                                     displayValue = `${val} ${valueSuffix}`.trim();
                                 } else {
                                     displayValue = `${pct}%`;
                                 }

                                return (
                                    <Stack key={item.name} direction="row" alignItems="center" gap={1}>
                                        <Box
                                            sx={{
                                                width: 20,
                                                height: 10,
                                                borderRadius: '2px',
                                                backgroundColor: colorMapping[item.name] || '#4A90E2',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: 'Montserrat, sans-serif',
                                                fontSize: '12px',
                                                color: '#444',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.name}{' '}
                                            <span style={{ fontWeight: 700, color: '#1a1a2e' }}>
                                                 {displayValue}
                                            </span>
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LicenseModelDistributionWidget;